import { type ReactNode, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { type IAdditionalField, type IDBOptionAdditionalField } from '../../../License/types/request';

import { type IComparisonField, type TAdditionalFieldPathPrefix } from '../../types';

import { versionComparisonSelector } from '../../../../store/audit/selectors';

import styles from './ComparisonField.module.scss';

interface IRenderComponentParams {
  value: unknown;
  valueHasChanged: boolean;
  isOldVersion: boolean;
}

interface IAdditionalFieldComparisonFieldProps<T, K extends keyof T>
  extends Pick<IComparisonField, 'label' | 'render' | 'className'> {
  differenceKey: 'leftValue' | 'rightValue';
  isOldVersion: boolean;
  values: T[];
  pathPrefix: TAdditionalFieldPathPrefix;
  index: number;
  fieldKey: K;
  id?: number;
  renderComponent?: (params: IRenderComponentParams) => ReactNode;
}

export function AdditionalFieldComparisonField<
  T extends IAdditionalField | IDBOptionAdditionalField,
  K extends keyof T
>({
  label,
  fieldKey,
  differenceKey,
  index,
  isOldVersion,
  render,
  className,
  values,
  pathPrefix,
  renderComponent,
}: IAdditionalFieldComparisonFieldProps<T, K>) {
  const { t } = useTranslation();
  const { differenceMap } = useSelector(versionComparisonSelector);

  const path = `${pathPrefix}/${index}/${fieldKey as string}`;
  const valueHasChanged = !!differenceMap[path];
  const value = valueHasChanged ? differenceMap[path]?.[differenceKey] : values[index]?.[fieldKey];

  const formattedValue = useMemo(() => {
    if (value == null || value === '') {
      return '-';
    }

    if (render) {
      return render(value);
    }

    return value;
  }, [render, value]);

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.label}>{t(label)}</div>
      {renderComponent ? (
        <div className={styles.valueContainer}>
          {renderComponent({
            value,
            valueHasChanged,
            isOldVersion,
          })}
        </div>
      ) : (
        <div
          className={cn(styles.valueContainer, {
            [styles.removed as string]: valueHasChanged && isOldVersion,
            [styles.added as string]: valueHasChanged && !isOldVersion,
          })}
        >
          {formattedValue as string}
        </div>
      )}
    </div>
  );
}
